import { SITE_CONFIG } from '@/lib/constants'
import type { SeoContent } from './types'

export const DEFAULT_SEO_KEYWORDS =
  'digital transformation Dubai, web development Dubai, mobile app development, AI solutions, Odoo ERP Dubai'

export const DEFAULT_ROBOTS =
  'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

export const DEFAULT_OG_IMAGE = `${SITE_CONFIG.url}/images/og-image.png`

export interface ResolvedSeo {
  title: string
  description: string
  keywords: string
  image: string
  url: string
  type: string
  robots: string
  favicon?: string
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

function pickFirst(...values: (string | undefined | null)[]): string {
  for (const value of values) {
    const text = typeof value === 'string' ? value.trim() : ''
    if (text) return text
  }
  return ''
}

function buildCanonical(pathname: string): string {
  const base = SITE_CONFIG.url.replace(/\/$/, '')
  if (!pathname || pathname === '/') return `${base}/`
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`
  return `${base}${path}`
}

export function resolveSeo(options: {
  cmsSeo?: SeoContent | null
  globalSeo?: SeoContent | null
  legacyTitle?: string
  legacyDescription?: string
  pathname?: string
  type?: string
  favicon?: string
  jsonLd?: ResolvedSeo['jsonLd']
}): ResolvedSeo {
  const pathname = options.pathname ?? '/'

  const title = pickFirst(
    options.cmsSeo?.metaTitle,
    options.globalSeo?.metaTitle,
    options.legacyTitle,
    SITE_CONFIG.title
  )

  const description = pickFirst(
    options.cmsSeo?.metaDescription,
    options.globalSeo?.metaDescription,
    options.legacyDescription,
    SITE_CONFIG.description
  )

  const keywords = pickFirst(
    options.cmsSeo?.keywords,
    options.globalSeo?.keywords,
    DEFAULT_SEO_KEYWORDS
  )

  const robots = pickFirst(
    options.cmsSeo?.metaRobots,
    options.globalSeo?.metaRobots,
    DEFAULT_ROBOTS
  )

  const image = pickFirst(
    options.cmsSeo?.ogImage,
    options.globalSeo?.ogImage,
    DEFAULT_OG_IMAGE
  )

  const url = pickFirst(
    options.cmsSeo?.canonicalUrl,
    options.globalSeo?.canonicalUrl,
    buildCanonical(pathname)
  )

  return {
    title,
    description,
    keywords,
    image,
    url,
    type: options.type ?? 'website',
    robots,
    favicon: options.favicon,
    jsonLd: options.jsonLd,
  }
}

export function buildServicePageJsonLd(page: {
  title: string
  description: string
  slug: string
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: page.title,
    description: page.description,
    url: `${SITE_CONFIG.url}/services/${page.slug}`,
    provider: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
    },
    areaServed: {
      '@type': 'Country',
      name: 'United Arab Emirates',
    },
  }
}
